import { Analytics } from '@cobliteam/analytics';
import type { UpdateDriverFaceClusterRequest } from '@cobliteam/common-types';
import { useAssignDriverFaceClusters } from '@cobliteam/hooks/src/useCamera/useDriverFaceCluster';
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import { useTranslation } from '@cobliteam/i18n';
import Alert from '@cobliteam/ui/components/Alert';
import Dialog from '@cobliteam/ui/components/Dialog';
import { DRIVER_FACE_PHOTO_MAX } from '@cobliteam/ui-business/components/drawers/DriverDrawer/constants';
import { subDays } from '@cobliteam/utils/date';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Counter } from './components/Counter';
import { EmptyState } from './components/EmptyState';
import { ErrorState } from './components/ErrorState';
import { FilterBar } from './components/FilterBar';
import { Footer } from './components/Footer';
import { LoadingGrid } from './components/LoadingGrid';
import { PhotoClusterCard } from './components/PhotoClusterCard';
import { SelectionPill } from './components/SelectionPill';
import { useDriverPhotoClusters } from './hooks/useDriverPhotoClusters';
import type { PhotoFilters } from './types';

const PAGE_SIZE = 12;

const defaultFilters = (): PhotoFilters => {
  const to = new Date();
  const from = subDays(to, 6);
  return { dateRange: { from, to } };
};

interface DriverPhotosSelectorProps {
  open: boolean;
  driverId: string;
  driverName: string;
  /** remaining slots out of DRIVER_FACE_PHOTO_MAX (based on existing photos already linked). */
  remainingSlots?: number;
  onClose: () => void;
  onAssigned: () => void;
}

export const DriverPhotosSelector = ({ open, driverId, driverName, remainingSlots = DRIVER_FACE_PHOTO_MAX, onClose, onAssigned }: DriverPhotosSelectorProps) => {
  const { t } = useTranslation('drivers');
  const [filters, setFilters] = useState<PhotoFilters>(defaultFilters);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const { setPosition } = useChatWidget();

  const { clusters, totalGroups, shownGroups, totalPages, isLoading, isError, refetch } = useDriverPhotoClusters({
    filters,
    page,
    pageSize: PAGE_SIZE,
  });

  const { mutateAsync: assignClusters, isPending: isSubmitting } = useAssignDriverFaceClusters();

  const isEmpty = !(isLoading || isError) && clusters.length === 0;
  const hasClusters = !(isLoading || isError || isEmpty);

  const selectedCount = selectedIds.size;
  const photoCountByClusterId = useMemo(() => new Map(clusters.map((cluster) => [cluster.id, cluster.photos.length])), [clusters]);
  const selectedPhotosCount = useMemo(
    () => Array.from(selectedIds).reduce((total, clusterId) => total + (photoCountByClusterId.get(clusterId) ?? 0), 0),
    [photoCountByClusterId, selectedIds]
  );

  useEffect(() => {
    if (open) {
      Analytics.track('Driver Photos Selector - Opened', { driverId });
      setPosition('default');
    }
  }, [open, driverId, setPosition]);

  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set());
    setFilters(defaultFilters());
    setPage(1);
  }, [open]);

  useEffect(() => {
    if (totalPages < 1) return;
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const toggle = useCallback(
    (clusterId: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(clusterId)) {
          next.delete(clusterId);
        } else {
          const selectedPhotos = Array.from(next).reduce((total, id) => total + (photoCountByClusterId.get(id) ?? 0), 0);
          const clusterPhotos = photoCountByClusterId.get(clusterId) ?? 0;
          if (selectedPhotos + clusterPhotos > remainingSlots) return prev;
          next.add(clusterId);
        }
        Analytics.track('Driver Photos Selector - Cluster Toggled', { driverId, clusterId, selected: !prev.has(clusterId) });
        return next;
      });
    },
    [driverId, photoCountByClusterId, remainingSlots]
  );

  const handleClearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const handleFiltersChange = useCallback(
    (next: PhotoFilters) => {
      Analytics.track('Driver Photos Selector - Filters Changed', { driverId, filters: next });

      setFilters(next);
      setSelectedIds(new Set());
      setPage(1);
    },
    [driverId]
  );

  const handleConfirm = useCallback(async () => {
    const assignments: UpdateDriverFaceClusterRequest[] = Array.from(selectedIds).map((clusterId) => ({
      driverFaceClusterId: clusterId,
      driverId,
    }));

    try {
      await assignClusters({ assignClusters: assignments });
      Analytics.track('Driver Photos Selector - Confirmed', { driverId, selectedCount: assignments.length });
      onAssigned();
    } catch (error) {
      Analytics.track('Driver Photos Selector - Assign Failed', { driverId, message: error instanceof Error ? error.message : String(error) });
      Alert.error(t('face_id.selector.error.assign_failed'));
    }
  }, [assignClusters, driverId, onAssigned, selectedIds, t]);

  const handleCancel = useCallback(() => {
    Analytics.track('Driver Photos Selector - Cancelled', { driverId, selectedCount: selectedIds.size });
    onClose();
  }, [driverId, onClose, selectedIds.size]);

  const resetFilters = useCallback(() => handleFiltersChange(defaultFilters()), [handleFiltersChange]);

  const isDisabledForDisplay = useMemo(
    () => (clusterId: string) => {
      if (selectedIds.has(clusterId)) return false;
      const clusterPhotos = photoCountByClusterId.get(clusterId) ?? 0;
      return selectedPhotosCount + clusterPhotos > remainingSlots;
    },
    [photoCountByClusterId, remainingSlots, selectedIds, selectedPhotosCount]
  );

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) handleCancel();
    setPosition(nextOpen ? 'default' : 'drawer');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content size='lg'>
        <Dialog.Header>
          <Dialog.Title>{t('face_id.selector.title', { driverName })}</Dialog.Title>
          <Dialog.Close onClick={handleCancel} />
        </Dialog.Header>
        <Dialog.Body className='space-y-4'>
          <FilterBar filters={filters} onFiltersChange={handleFiltersChange} onRefresh={refetch} />
          <Counter shown={shownGroups} total={totalGroups} onHelpOpen={() => Analytics.track('Driver Photos Selector - Help Opened', { driverId })} />
          <div className='min-h-[400px]'>
            {isLoading && <LoadingGrid />}
            {isError && <ErrorState onRetry={refetch} />}
            {isEmpty && <EmptyState onResetFilters={resetFilters} />}
            {hasClusters && (
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'>
                {clusters.map((cluster, i) => (
                  <PhotoClusterCard
                    key={cluster.id}
                    cluster={cluster}
                    index={i + 1}
                    total={clusters.length}
                    selected={selectedIds.has(cluster.id)}
                    disabled={isDisabledForDisplay(cluster.id)}
                    onToggle={() => toggle(cluster.id)}
                  />
                ))}
              </div>
            )}
          </div>
          <div className='pointer-events-none sticky bottom-4 z-10 flex justify-center'>
            <div className='pointer-events-auto'>
              <SelectionPill count={selectedCount} onClear={handleClearSelection} />
            </div>
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <Footer
            page={page}
            totalPages={totalPages}
            selectedCount={selectedCount}
            selectedPhotosCount={selectedPhotosCount}
            isSubmitting={isSubmitting}
            onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
            onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
          />
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};

export default DriverPhotosSelector;
